// Multi-tenant support: each company has its own column mapping + rate table.
// Data now lives in Supabase (table: public.tenants). See src/api.js for the wrapper.
import React from 'react';
import { Icon, Pill, Button, Card } from './ui.jsx';
import { parseSpreadsheet, parseARFile, normKey } from './admin.jsx';
import { api } from './api.js';
import { hasPermission } from './auth.jsx';

// Active tenant is a small UI preference (which tenant is selected in the
// picker). Keeping it in localStorage is fine — it's not security-sensitive
// and losing it just resets to the first tenant.
const ACTIVE_TENANT_KEY = 'sc_active_tenant';

// Async — returns a Promise<Tenant[]>. Callers must use `await` or `.then()`.
export async function loadTenants() {
  return api.tenants.list();
}

// Async — creates (or updates) a tenant row in the database.
// If `tenant.id` is present we update; otherwise we insert.
export async function saveTenant(tenant) {
  if (tenant.id && typeof tenant.id === 'string' && tenant.id.length >= 36) {
    // UUID → existing row → update.
    return api.tenants.update(tenant.id, tenant);
  }
  return api.tenants.create(tenant);
}

export function getActiveTenantId() {
  return localStorage.getItem(ACTIVE_TENANT_KEY);
}
export function setActiveTenantId(id) {
  if (id) localStorage.setItem(ACTIVE_TENANT_KEY, id);
}

// ================ Tenant picker (header dropdown) ================
export function TenantPicker({ tenants, activeId, onSelect, onNew }) {
  const [open, setOpen] = React.useState(false);
  const active = tenants.find(t => t.id === activeId) || tenants[0];
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="h-8 px-3 inline-flex items-center gap-2 rounded-md border border-neutral-200 bg-white text-[12px] font-medium text-neutral-700 hover:border-neutral-300">
        <Icon name="runs" size={13}/>
        <span className="text-neutral-900">{active ? active.name : 'No tenant'}</span>
        <Icon name="chevronDown" size={12} className="text-neutral-400"/>
      </button>
      {open && (
        <div className="absolute top-10 right-0 w-[280px] bg-white rounded-md shadow-lg border border-neutral-200 z-50 overflow-hidden">
          <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 bg-canvas/60 border-b border-neutral-200">Tenants</div>
          <div className="max-h-[260px] overflow-y-auto">
            {tenants.map(t => (
              <button key={t.id} onClick={() => { onSelect(t.id); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-[13px] flex items-center justify-between hover:bg-neutral-50 ${t.id === activeId ? 'bg-primary-100/40' : ''}`}>
                <div>
                  <div className="font-medium text-neutral-900">{t.name}</div>
                  <div className="text-[11px] text-neutral-500">{t.entities.length} {t.entities.length === 1 ? 'entity' : 'entities'} · Y1 {(t.rates.y1*100).toFixed(0)}% / Y2+ {(t.rates.y2*100).toFixed(0)}%</div>
                </div>
                {t.id === activeId && <Icon name="check" size={14} className="text-primary-700"/>}
              </button>
            ))}
          </div>
          <button onClick={() => { onNew(); setOpen(false); }}
            className="w-full px-3 py-2 text-[13px] font-medium text-primary-700 hover:bg-primary-100/40 border-t border-neutral-200 inline-flex items-center gap-2">
            <Icon name="plus" size={13}/> New tenant…
          </button>
        </div>
      )}
    </div>
  );
}

// ================ S25 · Edit tenant ================
// Lets an admin update the editable parts of an existing tenant: name and
// entity list. Rates are intentionally read-only here — the engine reads
// from public.commission_plans, not tenant.rates, so editing rates here
// would mislead. Column mappings require re-uploading sample files and
// will get their own flow later.
export function ScreenEditTenant({ tenants, activeId, onNav, onSave }) {
  const tenant = tenants.find(t => t.id === activeId) || tenants[0];

  const [name, setName]         = React.useState(tenant?.name || '');
  const [entities, setEntities] = React.useState(tenant?.entities && tenant.entities.length ? [...tenant.entities] : ['']);
  const [arDocFormat, setArDocFormat] = React.useState(tenant?.arDocumentFormat || 'branchInitials+invoiceNumber');
  // Store as a string so an empty box stays empty; convert on save.
  const [gpOverride, setGpOverride] = React.useState(
    tenant?.grossProfitOverride == null ? '' : String(tenant.grossProfitOverride * 100)
  );
  const [saving, setSaving]     = React.useState(false);
  const [error, setError]       = React.useState(null);

  // Keep local state in sync if the parent swaps which tenant is active
  // while this screen is mounted (rare, but possible via the picker).
  React.useEffect(() => {
    setName(tenant?.name || '');
    setEntities(tenant?.entities && tenant.entities.length ? [...tenant.entities] : ['']);
    setArDocFormat(tenant?.arDocumentFormat || 'branchInitials+invoiceNumber');
    setGpOverride(tenant?.grossProfitOverride == null ? '' : String(tenant.grossProfitOverride * 100));
    setError(null);
  }, [tenant?.id]);

  if (!tenant) {
    return (
      <div className="p-10 text-center text-neutral-500 text-[13px]">
        No tenant to edit. <button onClick={() => onNav('s20')} className="text-primary-700 font-medium">Back to tenants</button>
      </div>
    );
  }

  const cleanEntities = entities.map(e => e.trim()).filter(Boolean);

  const save = async () => {
    setError(null);
    if (!name.trim()) { setError('Tenant name is required.'); return; }
    if (cleanEntities.length === 0) { setError('At least one entity is required.'); return; }
    setSaving(true);
    try {
      // Partial patch — only the fields that are editable here. saveTenant()
      // in App.jsx is shared with the create wizard, but tenantToRow in
      // api.js is a partial-patch mapper, so unspecified fields stay put.
      const gpTrim = String(gpOverride).trim();
      const gpValue = gpTrim === '' ? null : (Number(gpTrim) / 100);
      await onSave({
        id: tenant.id,
        name: name.trim(),
        entities: cleanEntities,
        arDocumentFormat: arDocFormat,
        grossProfitOverride: gpValue,
      });
      onNav('s20');
    } catch (err) {
      setError('Save failed: ' + (err.message || err));
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-[820px] mx-auto space-y-5">
      <div>
        <div className="text-[12px] text-neutral-500 font-semibold uppercase tracking-[0.06em]">Tenant settings</div>
        <h1 className="text-[24px] font-bold text-neutral-900 tracking-[-0.01em] mt-1">Edit tenant</h1>
        <div className="text-[13px] text-neutral-500 mt-1">Active tenant · <span className="font-mono text-neutral-700">{tenant.id}</span></div>
      </div>

      {/* Basics — editable */}
      <Card className="space-y-5">
        <div>
          <label className="block text-[12px] font-semibold text-neutral-700 mb-1.5">Tenant name</label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={saving}
            className="w-full h-10 px-3 text-[14px] rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 disabled:opacity-50"/>
        </div>
        <div>
          <label className="block text-[12px] font-semibold text-neutral-700 mb-1.5">Legal entities / staffing suppliers</label>
          <div className="space-y-1.5">
            {entities.map((e, i) => (
              <div key={i} className="flex gap-2">
                <input
                  value={e}
                  onChange={ev => setEntities(entities.map((x, j) => j === i ? ev.target.value : x))}
                  placeholder={`Entity ${i + 1}`}
                  disabled={saving}
                  className="flex-1 h-9 px-3 text-[13px] rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 disabled:opacity-50"/>
                {entities.length > 1 && (
                  <button onClick={() => setEntities(entities.filter((_, j) => j !== i))}
                    disabled={saving}
                    className="h-9 w-9 rounded-md border border-neutral-200 text-neutral-500 hover:bg-neutral-50 disabled:opacity-50">
                    <Icon name="close" size={13} className="mx-auto"/>
                  </button>
                )}
              </div>
            ))}
            <button onClick={() => setEntities([...entities, ''])}
              disabled={saving}
              className="text-[12px] text-primary-700 hover:text-primary-900 font-medium disabled:opacity-50">
              + Add another entity
            </button>
          </div>
          <div className="text-[11px] text-neutral-500 mt-2">A sales rep can have clients across multiple entities within the same tenant.</div>
        </div>

        {/* AR document format — how the AR upload matches invoices back
            to the txn file. Two shapes seen in the wild:
            - branchInitials+invoiceNumber (JobSource): AR "CR-9171" ↔ txn Carson/9171
            - invoiceNumberOnly (AllianceHR): AR "JOBS-0284" ↔ txn JOBS-0284
            Picked once per tenant; used by the engine to build match keys. */}
        <div className="border-t border-neutral-100 pt-4">
          <label className="block text-[12px] font-semibold text-neutral-700 mb-1.5">AR document number format</label>
          <div className="text-[11px] text-neutral-500 mb-3 leading-relaxed">
            How AR reconciles to the txn file. Pick <b>Invoice number only</b> when your invoices already have a global prefix
            (e.g. <span className="font-mono">JOBS-0284</span>). Pick <b>Branch initials + invoice number</b> when the AR document
            uses branch as a prefix (e.g. AR <span className="font-mono">CR-9171</span> ↔ txn Carson invoice <span className="font-mono">9171</span>).
          </div>
          <select
            value={arDocFormat}
            onChange={e => setArDocFormat(e.target.value)}
            disabled={saving}
            className="w-full h-10 px-3 text-[13px] rounded-md border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 disabled:opacity-50">
            <option value="branchInitials+invoiceNumber">Branch initials + invoice number (JobSource-style)</option>
            <option value="invoiceNumberOnly">Invoice number only (AllianceHR-style)</option>
          </select>
        </div>

        {/* GP normalization — a decimal like 10.5 means "recompute
            GP as 10.5% × TotalBill at parse time". Needed when the
            source system exports GP without burden (JobSource post
            June 2026, ~28% of bill), which would double commissions.
            Leave blank to use whatever the file exports. */}
        <div className="border-t border-neutral-100 pt-4">
          <label className="block text-[12px] font-semibold text-neutral-700 mb-1.5">Gross profit override (% of TotalBill)</label>
          <div className="text-[11px] text-neutral-500 mb-3 leading-relaxed">
            When set, the parser recomputes each invoice's GP as this percentage of TotalBill — bypassing whatever the source file exported.
            Use this when your payroll system exports raw GP without burden deducted (e.g. new JobSource feed exports ~28% of bill instead of the historical ~10.5%).
            Leave blank to trust the file's GP.
          </div>
          <div className="flex items-center gap-2 max-w-[200px]">
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={gpOverride}
              onChange={e => setGpOverride(e.target.value)}
              disabled={saving}
              placeholder="e.g. 10.5"
              className="flex-1 h-10 px-3 text-[13px] rounded-md border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 disabled:opacity-50"
            />
            <span className="text-[13px] text-neutral-600">%</span>
          </div>
        </div>
      </Card>

      {/* Rates — pointer to Commission plans */}
      <Card>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="text-[12px] font-semibold text-neutral-700">Commission rates</div>
            <div className="text-[12px] text-neutral-600 mt-1.5 leading-relaxed max-w-[540px]">
              Rates, deductions (ACA / sick), tier ladders and quota gates now live on
              <b> commission plans</b>, so changes apply per plan and are reproducible across runs.
              Edit them on the Commission plans screen.
            </div>
            <div className="font-mono text-[12px] text-neutral-500 mt-2">
              Current seed rates (used when this tenant was created):{' '}
              ACA {(tenant.rates?.aca * 100).toFixed(2)}% · SICK {(tenant.rates?.sick * 100).toFixed(2)}% · Y1 {(tenant.rates?.y1 * 100).toFixed(0)}% · Y2+ {(tenant.rates?.y2 * 100).toFixed(0)}%
            </div>
          </div>
          <Button variant="secondary" size="sm" icon="arrow" onClick={() => onNav('s14')}>Open plans</Button>
        </div>
      </Card>

      {/* Column mappings — pointer back to wizard */}
      <Card>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="text-[12px] font-semibold text-neutral-700">Column mappings (Transaction · AR)</div>
            <div className="text-[12px] text-neutral-600 mt-1.5 leading-relaxed max-w-[540px]">
              Mappings tell the parser which column in your export holds invoice number, gross profit, etc.
              Re-mapping requires a sample file to read column headers from. Until that flow lands inline here,
              create a new tenant from the wizard with the updated export, then archive this one.
            </div>
            <div className="text-[12px] text-neutral-500 mt-2">
              Current: <b>{Object.values(tenant.txnMapping || {}).filter(Boolean).length}</b> Txn columns mapped ·{' '}
              <b>{Object.values(tenant.arMapping || {}).filter(Boolean).length}</b> AR columns mapped.
            </div>
          </div>
        </div>
      </Card>

      {error && <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={() => onNav('s20')} disabled={saving}>Cancel</Button>
        <Button variant="primary" icon="check" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
}

// ================ S20 · Tenants list ================
export function ScreenTenantsList({ tenants, activeId, onNav, onSelect, onDelete, currentUser }) {
  const canCreate = hasPermission(currentUser, 'tenants', 'create');
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-[24px] font-bold text-neutral-900 tracking-[-0.01em]">Tenants</h1>
          <div className="text-[13px] text-neutral-500 mt-1">Each tenant has its own column mapping, entity list, and rate structure.</div>
        </div>
        {canCreate && <Button variant="primary" icon="plus" onClick={() => onNav('s21')}>New tenant</Button>}
      </div>
      <Card padding="p-0">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-neutral-500 bg-canvas/60 border-b border-neutral-200">
              <th className="px-4 py-2.5 font-semibold">Tenant</th>
              <th className="px-4 py-2.5 font-semibold">Entities</th>
              <th className="px-4 py-2.5 font-semibold">Rates (ACA / SICK / Y1 / Y2+)</th>
              <th className="px-4 py-2.5 font-semibold">Created</th>
              <th className="px-4 py-2.5 w-24"/>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {tenants.map(t => (
              <tr key={t.id} className={`${t.id === activeId ? 'bg-primary-100/20' : ''} hover:bg-primary-100/30`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-neutral-900">{t.name}</div>
                  <div className="text-[11px] text-neutral-500 font-mono">{t.id}</div>
                </td>
                <td className="px-4 py-3 text-neutral-700">{t.entities.length}</td>
                <td className="px-4 py-3 font-mono text-[12px] text-neutral-700">
                  {(t.rates.aca*100).toFixed(2)}% / {(t.rates.sick*100).toFixed(2)}% / {(t.rates.y1*100).toFixed(0)}% / {(t.rates.y2*100).toFixed(0)}%
                </td>
                <td className="px-4 py-3 text-neutral-700">{t.createdAt}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-3">
                    {t.id === activeId
                      ? <Pill tone="finalized">Active</Pill>
                      : <button onClick={() => onSelect(t.id)} className="text-[12px] text-primary-700 hover:text-primary-900 font-medium">Activate</button>}
                    <button
                      onClick={() => { onSelect(t.id); onNav('s25'); }}
                      className="text-[12px] text-neutral-600 hover:text-neutral-900 font-medium inline-flex items-center gap-1"
                      title="Edit this tenant">
                      <Icon name="override" size={12}/> Edit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ================ S21 · New tenant wizard ================
export function ScreenNewTenant({ onNav, onSave }) {
  const [step, setStep]       = React.useState(1);
  const [name, setName]       = React.useState('');
  const [entities, setEntities] = React.useState(['']);
  const [rates, setRates]     = React.useState({ aca: 1.8, sick: 1.2, y1: 5, y2: 7 });
  const [txnSample, setTxnSample] = React.useState(null);
  const [arSample, setArSample]   = React.useState(null);
  const [txnMap, setTxnMap]   = React.useState({});
  const [arMap, setArMap]     = React.useState({});
  const [error, setError]     = React.useState(null);
  const txnInputRef = React.useRef(null);
  const arInputRef  = React.useRef(null);

  const txnFields = [
    ['customerName',     'Customer name',                true],
    ['invoiceBranch',    'Invoice branch',               true],
    ['invoiceNumber',    'Invoice number',               true],
    ['invoiceDate',      'Invoice date',                 true],
    ['totalBill',        'Total bill',                   true],
    ['grossWages',       'Gross wages',                  true],
    ['grossProfit',      'Gross profit',                 true],
    ['entity',           'Staffing supplier / entity',   false],
    ['customerId',       'Customer ID (sub-site / raw)', false],
    ['rootCustomerId',   'Root customer ID (parent)',    false],
  ];
  const arFields = [
    ['customerName', 'Customer name',  true],
    ['customerId',   'Customer ID',    false],
    ['documentNo',   'Document number',true],
    ['invoiceDate',  'Invoice date',   true],
    ['dueDate',      'Due date',       false],
    ['daysAged',     'Days aged',      false],
    ['total',        'Total outstanding', true],
  ];

  const handleTxnFile = async (f) => {
    if (!f) return;
    setError(null);
    try {
      const rows = await parseSpreadsheet(f);
      const cols = Object.keys(rows[0] || {});
      setTxnSample({ name: f.name, rowCount: rows.length, columns: cols, rows });
      // auto-suggest mappings.
      //   rootCustomerId → prefer Root*Customer*ID columns (parent ID).
      //   customerId     → the sub/raw ID; avoid Root* (those belong above).
      const suggest = {};
      txnFields.forEach(([key]) => {
        let match;
        if (key === 'rootCustomerId') {
          match = cols.find(c => /^root.*customer.*id$/i.test(c))
               || cols.find(c => normKey(c).includes('rootcustomer'))
               || cols.find(c => normKey(c).startsWith('root') && normKey(c).includes('id'));
        } else if (key === 'customerId') {
          match = cols.find(c => normKey(c) === 'customerid' && !/^root/i.test(c))
               || cols.find(c => normKey(c).includes('customerid') && !/^root/i.test(c));
        } else {
          match = cols.find(c => normKey(c) === normKey(key))
               || cols.find(c => normKey(c).includes(normKey(key)));
        }
        if (match) suggest[key] = match;
      });
      setTxnMap(suggest);
    } catch (err) { setError('Parse error: ' + err.message); }
  };

  const handleArFile = async (f) => {
    if (!f) return;
    setError(null);
    try {
      const rows = await parseARFile(f);
      const cols = Object.keys(rows[0] || {});
      setArSample({ name: f.name, rowCount: rows.length, columns: cols, rows });
      const suggest = {};
      arFields.forEach(([key]) => {
        const match = cols.find(c => normKey(c) === normKey(key)) || cols.find(c => normKey(c).includes(normKey(key)));
        if (match) suggest[key] = match;
      });
      setArMap(suggest);
    } catch (err) { setError('Parse error: ' + err.message); }
  };

  const save = () => {
    const tenant = {
      id: name.toLowerCase().replace(/[^a-z0-9]+/g,'-').slice(0, 30) || ('tenant-' + Date.now()),
      name: name.trim(),
      entities: entities.filter(e => e.trim()),
      rates: { aca: rates.aca/100, sick: rates.sick/100, y1: rates.y1/100, y2: rates.y2/100 },
      txnMapping: txnMap,
      arMapping: arMap,
      arDocumentFormat: 'branchInitials+invoiceNumber',
      createdAt: new Date().toISOString().slice(0,10),
    };
    onSave(tenant);
  };

  const stepLabels = ['Basics', 'Rates', 'Txn columns', 'AR columns', 'Review'];

  return (
    <div className="max-w-[820px] mx-auto">
      <div className="mb-2 text-[12px] text-neutral-500 font-semibold uppercase tracking-[0.06em]">Tenant setup</div>
      <h1 className="text-[24px] font-bold text-neutral-900 tracking-[-0.01em] mb-6">Create a new tenant</h1>

      <div className="flex items-center gap-2 mb-6">
        {stepLabels.map((l, i) => {
          const n = i + 1;
          const done = n < step, active = n === step;
          return (
            <React.Fragment key={l}>
              <div className={`flex items-center gap-2 h-9 px-3 rounded-full border text-[12px] font-medium
                  ${active ? 'border-primary-700 bg-primary-100 text-primary-900' :
                    done ? 'border-emerald-300 bg-emerald-50 text-emerald-800' :
                    'border-neutral-200 bg-white text-neutral-500'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold
                  ${active ? 'bg-primary-700 text-white' : done ? 'bg-emerald-600 text-white' : 'bg-neutral-200 text-neutral-600'}`}>
                  {done ? <Icon name="check" size={11}/> : n}
                </span>
                {l}
              </div>
              {i < stepLabels.length - 1 && <div className="w-4 h-px bg-neutral-200"/>}
            </React.Fragment>
          );
        })}
      </div>

      <input ref={txnInputRef} type="file" accept=".xlsx,.xls,.csv" style={{display:'none'}} onChange={e => handleTxnFile(e.target.files[0])}/>
      <input ref={arInputRef}  type="file" accept=".xlsx,.xls,.csv,.html,.htm" style={{display:'none'}} onChange={e => handleArFile(e.target.files[0])}/>

      {step === 1 && (
        <Card className="space-y-5">
          <div>
            <label className="block text-[12px] font-semibold text-neutral-700 mb-1.5">Tenant name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Acme Staffing Holdings"
              className="w-full h-10 px-3 text-[14px] rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-neutral-700 mb-1.5">Legal entities / staffing suppliers</label>
            <div className="space-y-1.5">
              {entities.map((e, i) => (
                <div key={i} className="flex gap-2">
                  <input value={e} onChange={ev => setEntities(entities.map((x,j) => j === i ? ev.target.value : x))}
                    placeholder={`Entity ${i+1}`}
                    className="flex-1 h-9 px-3 text-[13px] rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>
                  {entities.length > 1 && (
                    <button onClick={() => setEntities(entities.filter((_,j) => j !== i))} className="h-9 w-9 rounded-md border border-neutral-200 text-neutral-500 hover:bg-neutral-50">
                      <Icon name="close" size={13} className="mx-auto"/>
                    </button>
                  )}
                </div>
              ))}
              <button onClick={() => setEntities([...entities, ''])} className="text-[12px] text-primary-700 hover:text-primary-900 font-medium">+ Add another entity</button>
            </div>
            <div className="text-[11px] text-neutral-500 mt-1">A sales rep can have clients across multiple entities within the same tenant.</div>
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card className="space-y-4">
          <div className="text-[13px] font-semibold text-neutral-900">Commission rate structure</div>
          <div className="grid grid-cols-2 gap-4">
            {[['aca','ACA rate (applied to Gross Wages)'],
              ['sick','SICK rate (applied to Gross Wages)'],
              ['y1','Year 1 rate (new client)'],
              ['y2','Year 2+ rate (returning client)']].map(([k, l]) => (
              <div key={k}>
                <label className="block text-[12px] font-semibold text-neutral-700 mb-1.5">{l}</label>
                <div className="relative">
                  <input type="number" step="0.1" value={rates[k]} onChange={e => setRates({...rates, [k]: parseFloat(e.target.value) || 0})}
                    className="w-full h-9 pl-3 pr-8 text-[13px] font-mono rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-neutral-400 font-mono">%</span>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-neutral-100 pt-3 text-[12px] text-neutral-500 leading-relaxed">
            Commission per invoice = (GP − GrossWages × ACA% − GrossWages × SICK%) × tier rate.
            Tier is <b>Y1</b> for the first 12 months of a new client, <b>Y2+</b> after.
          </div>
        </Card>
      )}

      {step === 3 && (
        <div className="space-y-4">
          {!txnSample ? (
            <Card padding="p-8">
              <div onClick={() => txnInputRef.current && txnInputRef.current.click()}
                   className="border-2 border-dashed border-neutral-300 rounded-lg py-14 flex flex-col items-center justify-center text-center hover:border-primary-700 hover:bg-primary-100/20 cursor-pointer">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-3">
                  <Icon name="upload" size={20} className="text-primary-700"/>
                </div>
                <div className="text-[15px] font-semibold text-neutral-900">Upload a sample Transaction export</div>
                <div className="text-[13px] text-neutral-500 mt-1">We'll read the column headers so you can map them.</div>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[13px] font-semibold text-neutral-900">{txnSample.name}</div>
                  <div className="text-[11px] text-neutral-500">{txnSample.rowCount.toLocaleString()} rows · {txnSample.columns.length} columns</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setTxnSample(null)}>Replace</Button>
              </div>
              <div className="text-[13px] font-semibold text-neutral-900 mb-2">Map columns</div>
              <div className="space-y-2">
                {txnFields.map(([key, label, req]) => (
                  <div key={key} className="grid grid-cols-[200px_1fr] items-center gap-3">
                    <div className="text-[13px] text-neutral-700">
                      {label} {req && <span className="text-red-600">*</span>}
                    </div>
                    <select value={txnMap[key] || ''} onChange={e => setTxnMap({...txnMap, [key]: e.target.value})}
                      className="h-9 px-2 text-[13px] rounded-md border border-neutral-300 bg-white">
                      <option value="">— not mapped —</option>
                      {txnSample.columns.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {error && <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          {!arSample ? (
            <Card padding="p-8">
              <div onClick={() => arInputRef.current && arInputRef.current.click()}
                   className="border-2 border-dashed border-neutral-300 rounded-lg py-14 flex flex-col items-center justify-center text-center hover:border-primary-700 hover:bg-primary-100/20 cursor-pointer">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-3">
                  <Icon name="upload" size={20} className="text-primary-700"/>
                </div>
                <div className="text-[15px] font-semibold text-neutral-900">Upload a sample AR / Aging export</div>
                <div className="text-[13px] text-neutral-500 mt-1">Supports .xlsx, .csv, and HTML-style Sage exports.</div>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[13px] font-semibold text-neutral-900">{arSample.name}</div>
                  <div className="text-[11px] text-neutral-500">{arSample.rowCount.toLocaleString()} rows · {arSample.columns.length} columns</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setArSample(null)}>Replace</Button>
              </div>
              <div className="text-[13px] font-semibold text-neutral-900 mb-2">Map columns</div>
              <div className="space-y-2">
                {arFields.map(([key, label, req]) => (
                  <div key={key} className="grid grid-cols-[200px_1fr] items-center gap-3">
                    <div className="text-[13px] text-neutral-700">
                      {label} {req && <span className="text-red-600">*</span>}
                    </div>
                    <select value={arMap[key] || ''} onChange={e => setArMap({...arMap, [key]: e.target.value})}
                      className="h-9 px-2 text-[13px] rounded-md border border-neutral-300 bg-white">
                      <option value="">— not mapped —</option>
                      {arSample.columns.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-[12px] text-neutral-500 border-t border-neutral-100 pt-3">
                <b>Document number format:</b> branch initials + invoice number (used to match AR rows to transactions).
              </div>
            </Card>
          )}
          {error && <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
        </div>
      )}

      {step === 5 && (
        <Card className="space-y-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Tenant</div>
            <div className="text-[16px] font-semibold text-neutral-900">{name || '(unnamed)'}</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-1">Entities ({entities.filter(e=>e.trim()).length})</div>
            <div className="flex flex-wrap gap-1">
              {entities.filter(e=>e.trim()).map((e, i) => <span key={i} className="text-[11px] bg-neutral-100 rounded px-2 py-0.5">{e}</span>)}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-1">Rates</div>
            <div className="font-mono text-[13px] text-neutral-700">ACA {rates.aca}% · SICK {rates.sick}% · Y1 {rates.y1}% · Y2+ {rates.y2}%</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-1">Transaction columns mapped</div>
            <div className="text-[12px] text-neutral-700">{Object.values(txnMap).filter(Boolean).length} / {txnFields.length}</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-1">AR columns mapped</div>
            <div className="text-[12px] text-neutral-700">{Object.values(arMap).filter(Boolean).length} / {arFields.length}</div>
          </div>
        </Card>
      )}

      <div className="mt-5 flex items-center justify-between">
        <Button variant="ghost" onClick={() => step === 1 ? onNav('s20') : setStep(step - 1)}>{step === 1 ? 'Cancel' : 'Back'}</Button>
        {step < 5
          ? <Button variant="primary" icon="arrow" disabled={step === 1 && !name.trim()} onClick={() => setStep(step + 1)}>Next</Button>
          : <Button variant="success" icon="check" disabled={!name.trim()} onClick={save}>Create tenant</Button>}
      </div>
    </div>
  );
}

